import { createFileRoute, Link } from "@tanstack/react-router";
import { requireAuth } from "@/lib/route-guards";
import { LoadingProvider } from "@/contexts/loading-context";
import { handleApiError } from "@/lib/error-handler";
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Edit, Home, List, Menu, Play, Trophy } from "lucide-react";
import { SlidingNumber } from "@/components/ui/shadcn-io/sliding-number";
import { motion } from "motion/react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useKuraStartSignalR } from "@/hooks/use-kura-start-signalr";
import type { ParticipantDto } from "@/hooks/use-kura-start-signalr";
import { useKuraStartDetailQuery } from "@/hooks/use-kura-queries";
import { useSettingsQuery } from "@/hooks/use-settings-queries";
import { api, notifyStopDrawOnClose } from "@/lib/api";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import Marquee from "react-fast-marquee";

export const Route = createFileRoute("/kura/$kuraId/start-new-design-2")({
  beforeLoad: requireAuth,
  component: () => (
    <LoadingProvider>
      <KuraStartPage />
    </LoadingProvider>
  ),
});

function KuraStartPage() {
  const { kuraId } = Route.useParams();

  // Fetch kura start details from API
  const {
    data: kuraStartDetail,
    isLoading: isDetailLoading,
    error: detailError,
  } = useKuraStartDetailQuery(kuraId);

  // Counter states - these will update during the kura draw
  const [counter1, setCounter1] = useState(0); // Participant order number
  const [counter2, setCounter2] = useState(0); // Property order number

  // Recently drawn numbers (separate for participants and properties)
  const [recentNumbers1, setRecentNumbers1] = useState<number[]>([]); // Participant numbers
  const [recentNumbers2, setRecentNumbers2] = useState<number[]>([]); // Property numbers

  // Remaining participants count
  const [kalanHakSahibi, setKalanHakSahibi] = useState<number | null>(null);

  // Kura state
  const [kuraStarted, setKuraStarted] = useState(false);
  const [winners, setWinners] = useState<ParticipantDto[]>([]);

  // Ref to track if stop was requested
  const stopRequestedRef = useRef(false);

  // Ref to track if start toast has been shown
  const startToastShownRef = useRef(false);

  // State to track if we're in single draw mode (Ctrl+Y)
  const [isSingleDrawMode, setIsSingleDrawMode] = useState(false);

  // State to track if we're waiting for KuraStopped after single draw
  const [waitingForKuraStopped, setWaitingForKuraStopped] = useState(false);

  // Get lottery kind from API response
  const lotteryKind = kuraStartDetail?.lotteryKind || 1;
  // Kind 4 ("Katılımcı + Konut + Diğer + 65 Yaş") canlı çekilişte kind 2 ile aynı davranır;
  // farkı yalnızca konutun hangi katılımcıya gittiğidir.
  const isParticipantPropertyKind = lotteryKind === 2 || lotteryKind === 4;

  // Helper function to convert status number to Turkish text
  const getStatusText = (statusNum: number): string => {
    switch (statusNum) {
      case 0:
        return "Bekliyor"; // Pending
      case 1:
        return "Başlatıldı"; // Started
      case 2:
        return "Durduruldu"; // Stopped
      case 3:
        return "Bitti"; // Finished
      default:
        return "Bilinmiyor"; // Unknown
    }
  };

  // Helper function to calculate font size based on digit count (for big circles)
  const getFontSize = (num: number): string => {
    const digitCount = num.toString().length;
    if (digitCount <= 3) return "text-7xl"; // 72px - default for 3 digits or less
    if (digitCount === 4) return "text-6xl"; // 60px
    if (digitCount === 5) return "text-5xl"; // 48px
    if (digitCount === 6) return "text-4xl"; // 36px
    return "text-3xl"; // 30px - for 7+ digits
  };

  // Helper function for statistics section (smaller scale)
  const getStatsFontSize = (num: number): string => {
    const digitCount = num.toString().length;
    if (digitCount <= 3) return "text-4xl"; // 36px - default for 3 digits or less
    if (digitCount === 4) return "text-3xl"; // 30px
    if (digitCount === 5) return "text-2xl"; // 24px
    if (digitCount === 6) return "text-xl"; // 20px
    return "text-lg"; // 18px - for 7+ digits
  };

  // Fetch settings using React Query
  const { data: settings } = useSettingsQuery();

  // Marquee text from settings API
  const marqueeText = settings?.description || "";

  // Initialize KalanHakSahibi and winners from API data
  useEffect(() => {
    if (kuraStartDetail && kalanHakSahibi === null) {
      const initialValue =
        kuraStartDetail.kalanHakSahibi ?? kuraStartDetail.participantTotalCount;
      setKalanHakSahibi(initialValue);
    }

    // Initialize winners from resultList
    const list = kuraStartDetail?.resultList ?? [];
    if (list.length > 0 && list.length > winners.length) {
      setWinners(list);

      // Initialize recent numbers from the last 6 winners
      // For participant numbers
      const participantOrderNoColumn =
        kuraStartDetail?.getLotterySettingsList.find(
          (col) => col.isOrderNo && col.totalParticipants !== null
        );
      if (participantOrderNoColumn) {
        const columnName = participantOrderNoColumn.columnName;
        const lastSixNumbers = list
          .slice(0, 6) // Get first 6 (which are the most recent since list is latest first)
          .map((winner) => {
            const value = columnName ? winner[columnName] : null;
            const num = typeof value === "string" ? parseInt(value, 10) : value;
            return num || 0;
          })
          .filter((num) => num > 0)
          .reverse(); // Reverse to show oldest to newest

        if (lastSixNumbers.length > 0) {
          setRecentNumbers1(lastSixNumbers);
          // Set counter1 to the most recent (last in array)
          setCounter1(lastSixNumbers[lastSixNumbers.length - 1]);
        }
      }

      // For property numbers
      const propertyOrderNoColumn =
        kuraStartDetail?.getLotterySettingsList.find(
          (col) => col.isOrderNo && col.totalProperties !== null
        );
      if (propertyOrderNoColumn) {
        const columnName = propertyOrderNoColumn.columnName;
        const lastSixNumbers = list
          .slice(0, 6)
          .map((winner) => {
            const value = columnName ? winner[columnName] : null;
            const num = typeof value === "string" ? parseInt(value, 10) : value;
            return num || 0;
          })
          .filter((num) => num > 0)
          .reverse();

        if (lastSixNumbers.length > 0) {
          setRecentNumbers2(lastSixNumbers);
          setCounter2(lastSixNumbers[lastSixNumbers.length - 1]);
        }
      }
    }
  }, [kuraStartDetail, kalanHakSahibi, winners.length]);
  const getImageUrl = () => {
    if (kuraStartDetail?.lotteryImage) {
      // Backend "data:image" ile göndermediyse ekle
      if (!kuraStartDetail.lotteryImage.startsWith("data:image")) {
        return `data:image/png;base64,${kuraStartDetail.lotteryImage}`;
      }
      return kuraStartDetail.lotteryImage;
    }

    if (settings?.byteDosya) {
      return `data:image/png;base64,${settings.byteDosya}`;
    }

    return null;
  };
  const kuraImageUrl = getImageUrl();

  // Initialize SignalR connection for kura start
  const signalR = useKuraStartSignalR({
    kuraId,
    onNewWinner: (participant) => {
      // Only set kuraStarted to true if stop was not requested
      if (!stopRequestedRef.current) {
        setKuraStarted(true);
      }

      // Add the new winner at the beginning of the list (latest first)
      setWinners((prev) => [participant, ...prev]);

      // Update KalanHakSahibi value from backend SignalR response
      if (
        participant.KalanHakSahibi !== undefined &&
        participant.KalanHakSahibi !== null
      ) {
        const kalanValue =
          typeof participant.KalanHakSahibi === "string"
            ? parseInt(participant.KalanHakSahibi, 10)
            : participant.KalanHakSahibi;
        setKalanHakSahibi(kalanValue);
      }

      // Update counter1 to show current winner's Participant Sıra No
      const participantOrderNoColumn =
        kuraStartDetail?.getLotterySettingsList.find(
          (col) => col.isOrderNo && col.totalParticipants !== null
        );
      if (participantOrderNoColumn) {
        const columnName = participantOrderNoColumn.columnName;
        if (columnName && participant[columnName] !== undefined) {
          const siraNo = participant[columnName];
          const siraNoNumber =
            typeof siraNo === "string" ? parseInt(siraNo, 10) : siraNo;
          setCounter1(siraNoNumber);

          // Update recent numbers list (keep last 6)
          setRecentNumbers1((prev) => [...prev.slice(-5), siraNoNumber]);
        }
      }

      // Update counter2 to show current winner's Property Sıra No
      const propertyOrderNoColumn =
        kuraStartDetail?.getLotterySettingsList.find(
          (col) => col.isOrderNo && col.totalProperties !== null
        );
      if (propertyOrderNoColumn) {
        const columnName = propertyOrderNoColumn.columnName;
        if (columnName && participant[columnName] !== undefined) {
          const propertySiraNo = participant[columnName];
          const propertySiraNoNumber =
            typeof propertySiraNo === "string"
              ? parseInt(propertySiraNo, 10)
              : propertySiraNo;
          setCounter2(propertySiraNoNumber);

          // Update recent property numbers list (keep last 6)
          setRecentNumbers2((prev) => [
            ...prev.slice(-5),
            propertySiraNoNumber,
          ]);
        }
        else {
          setCounter2(0);
        }
      }
    },
    onKuraCompleted: () => {
      setKuraStarted(false);
      stopRequestedRef.current = false;
      // Reset single draw mode states
      setIsSingleDrawMode(false);
      setWaitingForKuraStopped(false);
      toast.success(
        <span className="text-xl font-extrabold whitespace-nowrap">
          KURA ÇEKİLİŞİ TAMAMLANDI
        </span>,
        {
          description: (
            <span className="text-xl font-semibold whitespace-nowrap">
              KURA TAMAMLANMIŞTIR.
            </span>
          ),
          duration: 5000,

        }
      );
    },
    onCounterUpdate: (counter1Val, counter2Val) => {
      if (counter1Val !== undefined) setCounter1(counter1Val);
      if (counter2Val !== undefined) setCounter2(counter2Val);
    },
    onKuraStopped: () => {
      // When KuraStopped is received after single draw
      setWaitingForKuraStopped(false);
      setIsSingleDrawMode(false);
      setKuraStarted(false);
      toast.info(
        <span className="text-xl font-semibold whitespace-nowrap">
          TEK ÇEKİLİŞ TAMAMLANDI
        </span>,
        {
          duration: 3000,
        }
      );
    },
  });

  // Notify backend when user leaves the page while kura is being drawn
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!kuraStarted) return;

      notifyStopDrawOnClose(kuraId);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [kuraId, kuraStarted]);

  // Keyboard shortcuts: Ctrl+B to start, Ctrl+M to stop kura, Ctrl+Y for single draw
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+B: Start kura
      if (event.ctrlKey && event.key === "b") {
        event.preventDefault();
        // Block if waiting for KuraStopped after single draw
        if (waitingForKuraStopped) {
          toast.warning("Lütfen tek çekiliş tamamlanmasını bekleyin.");
          return;
        }
        if (!kuraStarted) {
          handleStartKura(false); // SingleDraw = false
        }
        return;
      }

      // Ctrl+Y: Single draw (only for Sadece Katılımcı)
      if (event.ctrlKey && event.key === "y") {
        event.preventDefault();
        // Only available for lotteryKind 1 (Sadece Katılımcı)
        if (lotteryKind !== 1) {
          toast.warning("Tek çekiliş sadece 'Sadece Katılımcı' türündeki kuralar için kullanılabilir.");
          return;
        }
        // Block if waiting for KuraStopped
        if (waitingForKuraStopped) {
          toast.warning("Lütfen tek çekiliş tamamlanmasını bekleyin.");
          return;
        }
        // Block if kura is already started in continuous mode
        if (kuraStarted && !isSingleDrawMode) {
          toast.warning("Kura devam ederken tek çekiliş yapılamaz.");
          return;
        }
        handleSingleDraw();
        return;
      }

      // Ctrl+M: Stop kura
      if (event.ctrlKey && event.key === "m") {
        event.preventDefault();
        // Block if waiting for KuraStopped after single draw
        if (waitingForKuraStopped) {
          toast.warning("Tek çekiliş modu durdurulamaz. Lütfen çekilişin tamamlanmasını bekleyin.");
          return;
        }
        // Block if in single draw mode
        if (isSingleDrawMode) {
          toast.warning("Tek çekiliş modu durdurulamaz. Lütfen çekilişin tamamlanmasını bekleyin.");
          return;
        }
        if (kuraStarted) {
          handleStopKura();
        }
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kuraStarted, lotteryKind, waitingForKuraStopped, isSingleDrawMode]);

  // Slider texts - fixed texts for the slider
  const sliderTexts = [
    "Hak sahibi sıra numaraları TOPLU KONUT İDARESİ BAŞKANLIĞI resmi internet sayfasında bulunan duyurular bölümündeki ilgili projenin bilgilendirme bölümünde yayınlanmıştır.",
    "Kura çekilişlerimiz K.V.K.K. gereği isim ve kimlik numaraları gizli tutularak gerçekleştirilmektedir.",
    "Kura Çekilişlerimiz Ankara 66. Noterliği denetiminde yapılmaktadır.",
    "Kura çekiliş sonuçları noter onayından sonra TOPLU KONUT İDARESİ BAŞKANLIĞI resmi internet sayfası www.toki.gov.tr adresinden ilan edilecektir.",
  ];

  // Initialize Embla Carousel with autoplay and disabled interactions
  const [emblaRef] = useEmblaCarousel(
    {
      loop: true,
      watchDrag: false, // Disable mouse drag
      watchSlides: false, // Disable slide watching
    },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  );

  // Table columns configuration from API
  const allColumns = kuraStartDetail?.getLotterySettingsList || [];
  const tableColumns = allColumns.map((setting, index) => ({
    key: `${setting.label}_${index}`,
    label: setting.label,
    columnName: setting.columnName,
    index: index,
  }));

  // Get lottery name from API
  const lotteryName = kuraStartDetail?.lotteryName || "";

  // Function to start the kura draw - calls backend API
  const handleStartKura = async (singleDraw: boolean = false) => {
    try {
      // Reset flags when starting
      stopRequestedRef.current = false;
      startToastShownRef.current = false;

      // Set kuraStarted immediately to update UI
      setKuraStarted(true);

      startToastShownRef.current = true;

      // Determine the endpoint based on lotteryKind
      // Kind 4 kendi kuyruğunu ve tüketicisini kullanır.
      const endpoint =
        lotteryKind === 4
          ? `/lotteries/${kuraId}/start-realtype`
          : isParticipantPropertyKind
            ? `/lotteries/${kuraId}/start-participant-property`
            : `/lotteries/${kuraId}/start`;

      // Call backend REST API to start the draw with SingleDraw parameter
      const response = await api.post(endpoint, null, {
        params: { SingleDraw: singleDraw }
      });

      if (response.status !== 200) {
        throw new Error("Kura başlatılamadı");
      }
    } catch (error: any) {
      console.error("Error starting kura:", error);

      const errorMessage = handleApiError(error, "Kura başlatılamadı");
      toast.error(errorMessage);
      setKuraStarted(false);
    }
  };

  // Function to start single draw (Ctrl+Y) - only for Sadece Katılımcı
  const handleSingleDraw = async () => {
    try {
      // Reset flags when starting
      stopRequestedRef.current = false;
      startToastShownRef.current = false;

      // Set flags for single draw mode
      setIsSingleDrawMode(true);
      setWaitingForKuraStopped(true);
      setKuraStarted(true);

      // Only use /lotteries/{id}/start for single draw (lotteryKind 1)
      const endpoint = `/lotteries/${kuraId}/start`;

      // Call backend REST API with SingleDraw=true
      const response = await api.post(endpoint, null, {
        params: { SingleDraw: true }
      });

      if (response.status !== 200) {
        throw new Error("Tek çekiliş başlatılamadı");
      }

      toast.info(
        <span className="text-lg font-semibold whitespace-nowrap">
          TEK ÇEKİLİŞ BAŞLATILDI
        </span>,
        {
          duration: 2000,
        }
      );
    } catch (error: any) {
      console.error("Error starting single draw:", error);

      // Extract user-friendly error message
      const errorMessage = handleApiError(error, "Tek çekiliş başlatılamadı");
      toast.error(errorMessage);
      
      // Reset states on error
      setIsSingleDrawMode(false);
      setWaitingForKuraStopped(false);
      setKuraStarted(false);
    }
  };

  // Function to stop the kura draw
  const handleStopKura = async () => {
    try {
      // Prevent stopping if in single draw mode
      if (isSingleDrawMode || waitingForKuraStopped) {
        toast.warning("Tek çekiliş modu durdurulamaz. Lütfen çekilişin tamamlanmasını bekleyin.");
        return;
      }

      stopRequestedRef.current = true;

      if (!signalR.isConnected) {
        toast.error("Bağlantı kurulamadı.");
        return;
      }

      // Call backend REST API to stop the draw
      const response = await api.post(`/lotteries/${kuraId}/stop-draw`);

      if (response.status !== 200) {
        throw new Error("Kura durdurulamadı");
      }

      setKuraStarted(false);
      toast.success(
        <span className="text-2xl font-extrabold whitespace-nowrap">
          KURA ÇEKİLİŞİ DURDURULDU
        </span>,
        {
          duration: 5000,
          className: "p-12 flex items-center",
        }
      );
    } catch (error) {
      console.error("Error stopping kura:", error);
      const errorMessage = handleApiError(error, "Kura durdurulamadı");
      toast.error(errorMessage);
    }
  };

  // Loading state
  if (isDetailLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  // Show error state if data fetch failed
  if (detailError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-bold text-red-600">
            Veri yüklenirken hata oluştu
          </h2>
          <p className="text-gray-600">
            {detailError?.message || "Bilinmeyen hata"}
          </p>
          <Button onClick={() => window.location.reload()}>Tekrar Dene</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-[Inter] relative isolate">

      {/* Header - Dark Blue with Branding */}
      <div className="overflow-hidden relative isolate py-9 pb-15 px-6">
        <div className="column-wrapper isolate h-full relative   grid grid-cols-1 lg:grid-cols-12 gap-6    max-w-[600px] mx-auto    "
        >
          {/* Logo / Image sütunu */}
          <div className="lg:col-span-12 flex justify-center">
            {isDetailLoading || !kuraImageUrl ? (
              <Skeleton className="w-full h-[130px] rounded-lg" />
            ) : (
              <img
                src={kuraImageUrl || '../../lottery-start-logo.png'}
                alt="Kura Görseli"
                className="max-h-72 object-contain"
              />
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-full mx-auto px-6 pb-8 z-1 relative">
        <div className="xl:max-w-[1516px] mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10 relative items-start">
          {/* Left Column - Drawing Section */}
          <div className="lg:col-span-2 space-y-6 h-auto lg:sticky lg:top-5">
            {/* Current Drawing Card */}
            <div
              className="bg-white rounded-2xl p-8 h-full"
              style={{
                boxShadow:
                  "0px 12px 18px 0px #0000001A, 0px 5px 7px 0px #0000001A",
              }}
            >

              <div className="flex items-center justify-end mb-6">
                {kuraStartDetail?.status !== undefined && (
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-3 h-3 rounded-full ${kuraStartDetail.status === 1
                        ? "bg-green-500 animate-pulse"
                        : kuraStartDetail.status === 0
                          ? "bg-yellow-500"
                          : kuraStartDetail.status === 2
                            ? "bg-blue-500"
                            : kuraStartDetail.status === 3
                              ? "bg-red-500"
                              : "bg-gray-500"
                        }`}
                    ></div>
                    <span
                      className={`font-semibold ${kuraStartDetail.status === 1
                        ? "text-green-600"
                        : kuraStartDetail.status === 0
                          ? "text-yellow-600"
                          : kuraStartDetail.status === 2
                            ? "text-blue-600"
                            : kuraStartDetail.status === 3
                              ? "text-red-600"
                              : "text-gray-600"
                        }`}
                    >
                      {getStatusText(kuraStartDetail.status).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>

              {/* Big Circles - Current Numbers */}
              <div
                className={`flex justify-center mb-8 ${isParticipantPropertyKind ? "gap-8" : ""}`}
              >
                {/* Counter 1 - Participant Counter (show for lotteryKind 1 and 2) */}
                {(lotteryKind === 1 || isParticipantPropertyKind) && (
                  <div
                    className="overflow-hidden relative w-52 h-52 rounded-full bg-gradient-to-br from-[#212559] to-[#019E59] flex items-center justify-center"
                    style={{ boxShadow: "0px 36px 72px 0px #00000040" }}
                  >
                    <motion.div
                      layout
                      transition={{
                        type: "spring",
                        bounce: 0,
                        stiffness: 300,
                        damping: 30,
                      }}
                      className={`text-white ${getFontSize(counter1)} font-bold !h-[72px]`}
                    >
                      <SlidingNumber
                        number={counter1}
                        animateAll
                        className="h-full"
                      />
                    </motion.div>
                  </div>
                )}

                {/* Counter 2 - Property Counter (show for lotteryKind 2 and 3) */}
                {(isParticipantPropertyKind || lotteryKind === 3) && (
                  <div
                    className="relative w-52 h-52 rounded-full bg-gradient-to-br from-[#212559] to-[#019E59] flex items-center justify-center"
                    style={{ boxShadow: "0px 36px 72px 0px #00000040" }}
                  >
                    <motion.div
                      layout
                      transition={{
                        type: "spring",
                        bounce: 0,
                        stiffness: 300,
                        damping: 30,
                      }}
                      className={`text-white ${getFontSize(counter2)} font-bold !h-[72px]`}
                    >
                      <SlidingNumber
                        number={counter2}
                        animateAll
                        className="h-full"
                      />
                    </motion.div>
                  </div>
                )}
              </div>

              {/* Recent Numbers */}
              <div className="space-y-6">
                {/* Recent Participant Numbers (show for lotteryKind 1 and 2) */}
                {(lotteryKind === 1 || isParticipantPropertyKind) && (
                  <div>
                    <h4 className="text-[20px] font-semibold text-[#111827] pt-8 border-t border-[#E5E7EB]">
                      SON ÇEKİLEN NUMARALAR {isParticipantPropertyKind && "(KATILIMCI)"}
                    </h4>
                    <div className="flex gap-3 justify-start flex-wrap mt-5">
                      {recentNumbers1.length === 0 ? (
                        <p className="text-sm text-gray-500">
                          Kura henüz başlatılmadı.
                        </p>
                      ) : (
                        recentNumbers1.map((num, idx) => (
                          <div
                            key={idx}
                            className={`w-auto h-auto p-4 rounded-[10px] flex items-center justify-center font-semibold text-lg shadow-md ${idx === recentNumbers1.length - 1
                              ? "bg-[#019E59] text-white"
                              : "bg-[#E5E7EB] text-[#000000]"
                              }`}
                          >
                            {num}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Recent Property Numbers (show for lotteryKind 2 and 3) */}
                {(isParticipantPropertyKind || lotteryKind === 3) && (
                  <div>
                    <h4 className="text-[20px] font-semibold text-[#111827] pt-8 border-t border-[#E5E7EB]">
                      SON ÇEKİLEN NUMARALAR {isParticipantPropertyKind && "(KONUT)"}
                    </h4>
                    <div className="flex gap-3 justify-start flex-wrap mt-5">
                      {recentNumbers2.length === 0 ? (
                        <p className="text-sm text-gray-500">
                          Kura henüz başlatılmadı.
                        </p>
                      ) : (
                        recentNumbers2.map((num, idx) => (
                          <div
                            key={idx}
                            className={`w-auto h-auto p-4 rounded-[10px] flex items-center justify-center font-semibold text-lg shadow-md ${idx === recentNumbers2.length - 1
                              ? "bg-[#019E59] text-white"
                              : "bg-[#E5E7EB] text-[#000000]"
                              }`}
                          >
                            {num}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Info Sidebar */}
          <div className="space-y-7 sticky top-5 h-auto">
            {/* Project Info */}
            <div
              className="bg-white rounded-2xl p-7"
              style={{
                boxShadow:
                  "0px 12px 18px 0px #0000001A, 0px 5px 7px 0px #0000001A",
              }}
            >
              <h3 className="text-2xl font-bold text-[#111827] mb-6">
                {lotteryName}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-4xl font-bold text-[#2563EB] mb-2">
                    {kuraStartDetail?.participantTotalCount || 500}
                  </div>
                  <div className="text-base text-[#4B5563]">
                    Toplam Hak Sahibi
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-[#16A34A] mb-2">
                    {kuraStartDetail?.primaryCount || 0}
                  </div>
                  <div className="text-base text-[#4B5563]">Asil Sayısı</div>
                </div>
                <div className="text-center">
                  <motion.div
                    layout
                    transition={{
                      type: "spring",
                      bounce: 0,
                      stiffness: 300,
                      damping: 30,
                    }}
                    className={`${getStatsFontSize(kuraStartDetail?.backupCount || 0)} font-bold text-[#E51B23] mb-2 flex justify-center`}
                  >
                    <SlidingNumber
                      number={kuraStartDetail?.backupCount || 0}
                      animateAll
                    />
                  </motion.div>
                  <div className="text-base text-[#4B5563]">Yedek Sayısı</div>
                </div>
                <div className="text-center">
                  <motion.div
                    layout
                    transition={{
                      type: "spring",
                      bounce: 0,
                      stiffness: 300,
                      damping: 30,
                    }}
                    className={`${getStatsFontSize(kalanHakSahibi || 0)} font-bold text-[#E51B23] mb-2 flex justify-center`}
                  >
                    <SlidingNumber number={kalanHakSahibi || 0} animateAll />
                  </motion.div>
                  <div className="text-base text-[#4B5563]">
                    Kalan Hak Sahibi
                  </div>
                </div>
              </div>
            </div>

            {/* Slider */}
            <div
              className="bg-white rounded-2xl p-7"
              style={{
                boxShadow:
                  "0px 12px 18px 0px #0000001A, 0px 5px 7px 0px #0000001A",
              }}
            >
              {/* Embla Viewport - Non-interactive */}
              <div className="rounded-lg">
                <div
                  className="overflow-hidden h-full pointer-events-none"
                  ref={emblaRef}
                >
                  {/* Embla Container */}
                  <div className="flex h-full">
                    {sliderTexts.map((text, index) => (
                      <div
                        key={index}
                        className="flex-[0_0_100%] min-w-0 flex items-center justify-center"
                      >
                        <p className="text-sm font-medium leading-6 text-[#4B5563]">
                          {text}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Winners Table */}
        <div
          className="bg-white rounded-2xl overflow-hidden mt-11"
          style={{
            boxShadow: "0px 5px 7px 0px #0000001A, 0px 12px 18px 0px #0000001A",
            border: "1px solid #E5E7EB",
          }}
        >
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#0A0D33] hover:bg-[#0A0D33] !border-0">
                  {tableColumns.map((column) => (
                    <TableHead
                      key={column.key}
                      className="text-white font-semibold text-center py-5 text-[19px] whitespace-nowrap"
                    >
                      {column.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {winners.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={tableColumns.length}
                      className="text-center py-20"
                    >
                      <div>
                        <p className="text-sm font-semibold text-gray-700">
                          Kazananlar henüz belirlenmedi.
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                          Kura çekilişi sırasında kazananlar burada
                          görüntülenecektir.
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  winners.map((winner, winnerIndex) => (
                    <TableRow
                      key={winnerIndex}
                      className={
                        winnerIndex === 0
                          ? "bg-[#202459] hover:bg-[#202459] border-b border-[#3741514D]"
                          : "bg-[#fff] hover:bg-[#fff] border-b border-[#3741514D]"
                      }
                    >
                      {tableColumns.map((column) => {
                        const value = column.columnName
                          ? winner[column.columnName]
                          : null;

                        // Check if this is the "Durum" column for special styling
                        const columnNameLower =
                          column.columnName?.toLowerCase() || "";
                        const isDurumColumn =
                          columnNameLower.includes("durum") ||
                          columnNameLower.includes("status");
                        const primaryTitleLower = kuraStartDetail?.primaryTitle
                          ?.toString()
                          .toLowerCase();
                        const backupTitleLower = kuraStartDetail?.backupTitle
                          ?.toString()
                          .toLowerCase();
                        const currentValue = value?.toString().toLowerCase();

                        // Check if this is a numeric column (and not "Başvuru Türü")
                        // Apply JetBrains Mono font to numeric columns and numeric-like values (e.g., "3+1", "171,45")
                        const isBasvuruTuru =
                          columnNameLower.includes("basvuru") ||
                          columnNameLower.includes("başvuru") ||
                          columnNameLower.includes("application");
                        const valueStr = value?.toString() || "";
                        // Check if value contains only numbers and allowed symbols (+, -, ., ,, /, spaces)
                        // and does NOT contain any letters
                        const isNumericValue =
                          valueStr !== "" &&
                          value !== null &&
                          /\d/.test(valueStr) && // Contains at least one digit
                          /^[\d\s+\-.,/]+$/.test(valueStr); // Only digits, spaces, and symbols (+, -, ., ,, /)
                        const shouldUseMonoFont =
                          isNumericValue && !isBasvuruTuru && !isDurumColumn;

                        return (
                          <TableCell
                            key={column.key}
                            className="text-center font-medium py-6 px-4 text-[19px]"
                            style={{
                              color: winnerIndex === 0 ? "#fff" : "#000000",
                              fontFamily: shouldUseMonoFont
                                ? "'JetBrains Mono', monospace"
                                : undefined,
                            }}
                          >
                            {isDurumColumn && value ? (
                              <span
                                className={`px-4 py-1 rounded-full text-sm font-semibold ${winnerIndex === 0
                                  ? "bg-[#FFFFFF33] text-white border border-[#FFFFFF33]"
                                  : currentValue === primaryTitleLower
                                    ? "bg-[#10B98133] text-[#10B981] border border-[#10B9814D]"
                                    : currentValue === backupTitleLower
                                      ? "bg-[#B9850033] text-[#FBBF24] border border-[#FBBF244D]"
                                      : "bg-gray-100 text-gray-800"
                                  }`}
                              >
                                {value}
                              </span>
                            ) : column.columnName === "KuraNo" ? (
                              <span
                                className={
                                  winnerIndex === 0
                                    ? "text-white"
                                    : "text-[#019E59]"
                                }
                                style={{
                                  fontFamily: "'JetBrains Mono', monospace",
                                }}
                              >
                                {value ?? "-"}
                              </span>
                            ) : (
                              (value ?? "-")
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Sticky Footer with Marquee and FAB */}
      <div className="sticky bottom-4 z-50 mt-auto mx-4">
        <div className="flex items-center gap-4">
          {/* Marquee Section - flex-grow to take available space but with min-width 0 to allow shrinking */}
          <div
            className="flex-grow min-w-0 rounded-lg py-3 bg-[#202459]"
            style={{
              boxShadow: "0px 4px 11px 3px #7272721A",
            }}
          >
            <Marquee speed={50} gradient={false}>
              <span className="text-sm font-semibold leading-5 text-white mx-8">
                {marqueeText}
              </span>
            </Marquee>
          </div>

          {/* Button Section - fixed width to prevent being pushed out */}
          <div className="flex-shrink-0 w-12">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  size="icon"
                  className="rounded-full w-12 h-12 shadow-lg bg-[#202459] hover:bg-[#2A2E6C]"
                  aria-label="Menu"
                >
                  <Menu className="h-7 w-7" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                side="top"
                align="end"
                sideOffset={10}
                className="w-auto p-2"
              >
                <div className="flex flex-col gap-2">
                  {/* Homepage Button */}
                  <Button asChild variant="ghost" className="justify-start">
                    <Link to="/" className="flex items-center gap-2">
                      <Home className="h-4 w-4" />
                      Ana Sayfa
                    </Link>
                  </Button>

                  {/* Kura List Button */}
                  <Button asChild variant="ghost" className="justify-start">
                    <Link
                      to="/kura"
                      search={{ search: "" }}
                      className="flex items-center gap-2"
                    >
                      <List className="h-4 w-4" />
                      Kura Listesi
                    </Link>
                  </Button>

                  {/* Kura Detail Button */}
                  <Button asChild variant="ghost" className="justify-start">
                    <Link
                      to="/kura/$kuraId/edit"
                      params={{ kuraId }}
                      className="flex items-center gap-2"
                    >
                      <Edit className="h-4 w-4" />
                      Kura Detay
                    </Link>
                  </Button>

                  {/* Winners List Button */}
                  <Button asChild variant="ghost" className="justify-start">
                    <Link
                      to="/kura/$kuraId/winners"
                      params={{ kuraId }}
                      className="flex items-center gap-2"
                    >
                      <Trophy className="h-4 w-4" />
                      Kazanan Listesi
                    </Link>
                  </Button>

                  {/* Start Kura Button */}
                  {!kuraStarted ? (
                    <Button
                      variant="ghost"
                      className="justify-start"
                      onClick={() => handleStartKura(false)}
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Kurayı Başlat
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      className="justify-start text-red-600 hover:text-red-700"
                      onClick={handleStopKura}
                      disabled={isSingleDrawMode || waitingForKuraStopped}
                    >
                      <Play className="h-4 w-4 mr-2 rotate-180" />
                      {isSingleDrawMode || waitingForKuraStopped ? "Tek Çekiliş Devam Ediyor" : "Kurayı Durdur"}
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      <div className="fixed top-0 left-0 w-full h-full flex items-end pointer-events-none -z-10">
        <img
          src="../../lottery-start-bg.png"
          alt="Kura bg"
          className="w-full h-full object-cover"
        />
        <div
          className="h-[10%] w-full backdrop-blur-[4px] flex absolute left-0 bottom-0"
          style={{
            background: "linear-gradient(180deg, #191A48 0%, #040B1B 100%)",
          }}
        ></div>
      </div>
    </div>
  );
}
